import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  MenuItem,
  TablePagination,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import CustomTable from "components/custom-table/Table";
import { formatDate } from "utils/formatDate";
import { reviewPresentation } from "./reviewPresentation";
import DocumentStatus from "./components/DocumentStatus";
import DocumentReviewModal from "./components/DocumentReviewModal";
import {
  listReviewDocuments,
  getReviewCompanies,
} from "./service/DocumentReviewService";
import { useCompanyDocumentsLanguage } from "./i18n/CompanyDocumentsLanguageContext";

export default function DocumentReviewDocumentsTab({ setLayoutAction }) {
  const { t } = useCompanyDocumentsLanguage();

  const [filters, setFilters] = useState({
    status: "SUBMITTED",
    company_id: "",
    section: "",
  });
  const [page, setPage] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [data, setData] = useState({ documents: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    setLayoutAction(null);
  }, [setLayoutAction]);
  useEffect(() => {
    let active = true;
    getReviewCompanies()
      .then((c) => {
        if (active) {
          setCompanies(c);
        }
      })
      .catch((e) => active && setError(e.message));
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    listReviewDocuments({
      ...filters,
      company_id: filters.company_id || 0,
      page: page + 1,
      limit: 20,
    })
      .then((result) => active && setData(result))
      .catch((e) => active && setError(e.message))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [filters, page, refresh]);
  const filter = (key, value) => {
    setPage(0);
    setFilters({ ...filters, [key]: value });
  };
  return (
    <div className="bg-white shadow-sm rounded-lg p-5 grid gap-4 min-w-0">
      <div className="flex justify-between">
        <div>
          <h1 className="text-lg font-bold">
            {t("adminReview.documents.title")}
          </h1>
          <p className="text-sm text-[#5F737F]">
            {t("adminReview.documents.description")}
          </p>
        </div>
        <Button onClick={() => setRefresh(refresh + 1)}>
          {t("adminReview.common.refresh")}
        </Button>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <Autocomplete
          size="small"
          options={companies}
          value={
            companies.find((c) => c.company_id === filters.company_id) || null
          }
          getOptionLabel={(c) => `${c.name} (${c.effect_code || c.company_id})`}
          isOptionEqualToValue={(a, b) => a.company_id === b.company_id}
          onChange={(_, c) => filter("company_id", c?.company_id || "")}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("adminReview.common.issuer")}
              placeholder={t("adminReview.common.allIssuers")}
            />
          )}
        />
        <TextField select size="small" label={t("adminReview.documents.section")} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }} value={filters.section}
          onChange={(e) => filter("section", e.target.value)}>
          {[
            ["", t("adminReview.documents.allSections")],
            ["A", t("adminReview.documents.corporate")],
            ["C", t("adminReview.documents.management")],
          ].map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
        </TextField>
        <TextField
          select
          size="small"
          label={t("adminReview.common.status")}
          value={filters.status}
          onChange={(e) => filter("status", e.target.value)}
        >
          {[
            ["ALL", t("adminReview.common.all")],
            ["SUBMITTED", t("adminReview.common.submitted")],
            ["NEED_REVISION", t("adminReview.common.needRevision")],
            ["VERIFIED", t("adminReview.common.verified")],
          ].map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
      </div>

      {error && <Alert severity="error">{error}</Alert>}
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <CustomTable
          id="document-review-list"
          showPagination={false}
          headCells={[
            { id: "issuer", label: t("adminReview.common.issuer") },
            { id: "document", label: t("adminReview.documents.document") },
            { id: "context", label: t("adminReview.documents.contextOwner") },
            { id: "version", label: t("adminReview.common.version") },
            { id: "status", label: t("adminReview.common.status") },
            { id: "uploaded", label: t("adminReview.common.uploaded") },
            { id: "action", label: t("adminReview.common.action") },
          ]}
          bodyCells={data.documents.map((doc) => ({
            id: doc.id,
            issuer: (
              <div>
                <p>{doc.company_name}</p>
                <p className="text-xs text-[#5F737F]">{doc.company_code}</p>
              </div>
            ),
            document: reviewPresentation(doc, t).name,
            context: reviewPresentation(doc, t).context,
            version: `v${doc.version}`,
            status: <DocumentStatus status={doc.status} />,
            uploaded: (
              <div>
                <p>{formatDate.toFullDateTime(doc.uploaded_at)}</p>
                <p className="text-xs">
                  {doc.uploaded_by_name ||
                    t("adminReview.common.user", {
                      id: doc.uploaded_by,
                    })}
                </p>
              </div>
            ),
            action: (
              <Button onClick={() => setSelected(doc.id)}>
                {t("adminReview.common.review")}
              </Button>
            ),
          }))}
        />
      )}
      <TablePagination
        component="div"
        count={data.total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={20}
        rowsPerPageOptions={[20]}
      />
      {selected && (
        <DocumentReviewModal
          documentId={selected}
          onClose={() => setSelected(null)}
          onSaved={() => setRefresh((v) => v + 1)}
        />
      )}
    </div>
  );
}
